import {hasNonLinearKeyframes,hasValidKeyframeEasings} from './keyframe-easing';
import {hasSourceSceneGeneration,validateSourceSceneGenerationDocumentStructure} from './source-scene-generation';
import {validateSourceScriptDocument,hasSourceScript} from './source-script-data';
import {hasSourceBrandIntro,validateSourceBrandIntroDocument} from './source-brand-intro';
import {hasSourceSlides,validateSourceSlides} from './source-slide';
import {hasSourceText,validateSourceTextDocument} from './source-text';
import {hasStoryInfographics,hasCinematicMotion,validateCinematicMotionDocument} from './cinematic-motion';
import {hasCinematicMasks,validateCinematicMaskDocument} from './cinematic-masks';
import {hasTimelineAnimationWindows} from './animation-window';
import {hasPreservedGroupFrames} from './group-frame';
import {hasFadeWindows} from './fade-window';
import {hasCodeClocks} from './code-clock';
import {hasNativeCallLaneBars,validateNativeCallLaneBars} from './call-lane-bar';
/** Framework-free boundary shared by the Waka host and the separately bundled company editor.
 * Scope in a file is provenance, never proof of permission. The host must authorize the actual work row.
 */
export type JsonValue =
  | null
  | boolean
  | number
  | string
  | JsonValue[]
  | JsonObject;
export type JsonObject = { [key: string]: JsonValue };
export const NATIVE_EDITOR_FORMAT = "waka-company-editor" as const;
export const NATIVE_EDITOR_DOCUMENT_VERSION = 1 as const;
export const NATIVE_EDITOR_ANIMATION_DOCUMENT_VERSION = 2 as const;
export const NATIVE_EDITOR_GROUP_DOCUMENT_VERSION = 3 as const;
export function requiredDocumentVersion(document: unknown): 1|2|3|4|5|6|7|8|9|10 {
  return hasNonLinearKeyframes(document) ? 10 : (hasSourceSceneGeneration(document)||hasSourceScript(document)||hasSourceBrandIntro(document)||hasSourceSlides(document)||hasSourceText(document)||hasStoryInfographics(document)) ? 9 : hasNativeCallLaneBars(document) ? 8 : hasCinematicMotion(document) ? 7 : hasCinematicMasks(document) ? 6 : hasCodeClocks(document) ? 5 : hasFadeWindows(document) ? 4 : hasPreservedGroupFrames(document) ? 3 : hasTimelineAnimationWindows(document) ? 2 : 1;
}
export const NATIVE_EDITOR_RUNTIME = "4.0.471" as const;
export type NativeWorkScope = {
  projectId: string;
  workId: string;
  surface: "agent" | "editor";
};
export type NativeEditorEnvelope = {
  format: typeof NATIVE_EDITOR_FORMAT;
  schemaVersion: 2;
  engine: { id: "company-editor"; documentVersion: 1|2|3|4|5|6|7|8|9|10; remotionVersion: string };
  savedAt: string;
  sourceSnapshotHash: string | null;
  scope: NativeWorkScope | null;
  document: JsonObject;
  settings: JsonObject;
  sourceContext: JsonObject;
};
const record = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);
const identifier = (v: unknown): v is string =>
  typeof v === "string" &&
  v.length > 0 &&
  v.length <= 512 &&
  !["__proto__", "prototype", "constructor"].includes(v);

/** Clone only ordinary JSON data, retaining unknown fields. Reject lossy values instead of coercing them. */
export function copyEditorJson(value: unknown): JsonValue {
  const seen = new Set<object>();
  const budget = { nodes: 500000, characters: 16_000_000 };
  function copy(v: unknown, depth: number): JsonValue {
    if (--budget.nodes < 0 || depth > 64)
      throw new Error("편집 자료가 너무 크거나 복잡합니다.");
    if (v === null) return null;
    if (typeof v === "boolean") return v;
    if (typeof v === "number") {
      if (!Number.isFinite(v))
        throw new Error("편집 자료에 올바르지 않은 숫자가 있습니다.");
      return v;
    }
    if (typeof v === "string") {
      budget.characters -= v.length;
      if (budget.characters < 0) throw new Error("편집 자료가 너무 큽니다.");
      return v;
    }
    if (typeof v !== "object" || !v)
      throw new Error("저장할 수 없는 편집 값이 있습니다.");
    if (seen.has(v)) throw new Error("편집 자료가 순환 참조를 포함합니다.");
    seen.add(v);
    try {
      if (Object.getOwnPropertySymbols(v).length)
        throw new Error("기호 속성은 저장할 수 없습니다.");
      if (Array.isArray(v)) {
        const result: JsonValue[] = [];
        if (Object.keys(v).length !== v.length)
          throw new Error(
            "비어 있거나 추가 속성이 있는 배열은 저장할 수 없습니다.",
          );
        for (let i = 0; i < v.length; i++) {
          const descriptor = Object.getOwnPropertyDescriptor(v, String(i));
          if (!descriptor || !("value" in descriptor))
            throw new Error("계산 배열은 저장할 수 없습니다.");
          result.push(copy(descriptor.value, depth + 1));
        }
        return result;
      }
      const prototype = Object.getPrototypeOf(v);
      if (prototype !== Object.prototype && prototype !== null)
        throw new Error("일반 자료가 아닌 편집 값은 저장할 수 없습니다.");
      const out: JsonObject = {};
      for (const key of Object.keys(v)) {
        if (!identifier(key))
          throw new Error("올바르지 않은 편집 키가 있습니다.");
        budget.characters -= key.length;
        if (budget.characters < 0) throw new Error("편집 자료가 너무 큽니다.");
        const descriptor = Object.getOwnPropertyDescriptor(v, key);
        if (!descriptor || !("value" in descriptor))
          throw new Error("계산 속성은 편집 파일에 저장할 수 없습니다.");
        if (descriptor.value !== undefined)
          out[key] = copy(descriptor.value, depth + 1);
      }
      return out;
    } finally {
      seen.delete(v);
    }
  }
  return copy(value, 0);
}

/** JSON stores may reorder object keys; arrays and actual values still carry editorial meaning. */
export function editorValueSignature(value: unknown): string {
  return JSON.stringify(copyEditorJson(value), (_key, entry) => entry && typeof entry === 'object' && !Array.isArray(entry)
    ? Object.fromEntries(Object.entries(entry).sort(([left], [right]) => left < right ? -1 : left > right ? 1 : 0))
    : entry);
}

export type NativeEnvelopeRead =
  | { kind: "other" }
  | { kind: "invalid"; reason: string }
  | { kind: "unsupported"; reason: string }
  | { kind: "native"; envelope: NativeEditorEnvelope };

export function readNativeEnvelope(value: unknown): NativeEnvelopeRead {
  if (!record(value)) return { kind: "other" };
  const marker = Object.getOwnPropertyDescriptor(value, "format");
  if (marker && !("value" in marker))
    return { kind: "invalid", reason: "편집 형식은 일반 자료여야 합니다." };
  if (marker?.value !== NATIVE_EDITOR_FORMAT) return { kind: "other" };
  try {
    const raw = copyEditorJson(value);
    if (!record(raw) || raw.format !== NATIVE_EDITOR_FORMAT)
      return { kind: "invalid", reason: "편집 파일 형식이 올바르지 않습니다." };
    if (
      raw.schemaVersion !== 2 ||
      !record(raw.engine) ||
      raw.engine.id !== "company-editor" ||
      (![1,2,3,4,5,6,7,8,9,10].includes(Number(raw.engine.documentVersion))||typeof raw.engine.documentVersion!=='number')
    )
      return {
        kind: "unsupported",
        reason: "이 편집 파일은 다른 버전의 정밀 편집 화면이 필요합니다.",
      };
    if (
      !record(raw) ||
      !record(raw.engine) ||
      typeof raw.engine.remotionVersion !== "string" ||
      !/^\d+\.\d+\.\d+$/.test(raw.engine.remotionVersion) ||
      typeof raw.savedAt !== "string" ||
      !/^\d{4}-\d{2}-\d{2}T/.test(raw.savedAt) ||
      !Number.isFinite(Date.parse(raw.savedAt)) ||
      !(
        raw.sourceSnapshotHash === null || identifier(raw.sourceSnapshotHash)
      ) ||
      !record(raw.document) ||
      !record(raw.settings) ||
      !record(raw.sourceContext)
    )
      return {
        kind: "invalid",
        reason: "편집 파일의 버전이나 저장 정보가 올바르지 않습니다.",
      };
    if (
      raw.scope !== null &&
      (!record(raw.scope) ||
        !identifier(raw.scope.projectId) ||
        !identifier(raw.scope.workId) ||
        !["agent", "editor"].includes(String(raw.scope.surface)))
    )
      return {
        kind: "invalid",
        reason: "편집 파일의 작업 좌표가 올바르지 않습니다.",
      };
    if (Object.prototype.hasOwnProperty.call(raw, "state"))
      return {
        kind: "invalid",
        reason: "이전 편집 형식과 새 편집 형식이 섞여 있습니다.",
      };
    if(!validateSourceSceneGenerationDocumentStructure(raw.document))return {kind:'invalid',reason:'씬 설정과 보관한 영상 결과를 확인해 주세요.'};
    if(!validateSourceScriptDocument(raw.document))return {kind:'invalid',reason:'대본과 보관한 복원 원본을 확인해 주세요.'};
    if(!validateSourceBrandIntroDocument(raw.document))return {kind:'invalid',reason:'오프닝 카드 설정과 원본을 확인해 주세요.'};
    if(!validateSourceTextDocument(raw.document)||!validateSourceSlides(raw.document))return {kind:'invalid',reason:'화면 글자와 씬 연결을 확인해 주세요.'};
    if(!validateCinematicMotionDocument(raw.document))return {kind:'invalid',reason:'모션 카드 설정이 올바르지 않습니다.'};
    if(!validateCinematicMaskDocument(raw.document))return {kind:'invalid',reason:'모자이크 설정이 올바르지 않습니다.'};
    if(!hasValidKeyframeEasings(raw.document))return {kind:'invalid',reason:'키프레임 곡선 설정이 올바르지 않습니다.'};
    if(!validateNativeCallLaneBars(raw.document))return {kind:'invalid',reason:'통화 파형 설정이 올바르지 않습니다.'};
    if(Number(raw.engine.documentVersion)<requiredDocumentVersion(raw.document))
      return {kind:'invalid',reason:'컷 효과의 시간 정보와 편집 파일 버전이 맞지 않습니다. 원래 백업을 확인해 주세요.'};
    // The envelope is data only. A native reader still validates the document graph before mounting it.
    return { kind: "native", envelope: raw as NativeEditorEnvelope };
  } catch (error) {
    return {
      kind: "invalid",
      reason:
        error instanceof Error ? error.message : "편집 자료를 읽지 못했습니다.",
    };
  }
}

export function createNativeEnvelope(input: {
  document: unknown;
  settings: unknown;
  sourceContext?: unknown;
  scope?: NativeWorkScope | null;
  sourceSnapshotHash?: string | null;
  savedAt: string;
  extraEnvelope?: unknown;
}): NativeEditorEnvelope {
  const extra = copyEditorJson(input.extraEnvelope ?? {});
  const document = copyEditorJson(input.document);
  if (!record(extra))
    throw new Error("편집 파일 부가 정보가 올바르지 않습니다.");
  const result = readNativeEnvelope({
    ...extra,
    format: NATIVE_EDITOR_FORMAT,
    schemaVersion: 2,
    engine: {
      ...(record(extra.engine) ? extra.engine : {}),
      id: "company-editor",
      documentVersion: Math.max(requiredDocumentVersion(document),
        record(extra.engine)&&[1,2,3,4,5,6,7,8,9,10].includes(Number(extra.engine.documentVersion)) ? Number(extra.engine.documentVersion) : 1),
      remotionVersion: NATIVE_EDITOR_RUNTIME,
    },
    savedAt: input.savedAt,
    sourceSnapshotHash: input.sourceSnapshotHash ?? null,
    scope: input.scope ?? null,
    document,
    settings: input.settings,
    sourceContext: input.sourceContext ?? {},
  });
  if (result.kind !== "native")
    throw new Error(
      result.kind === "other"
        ? "편집 파일 형식을 확인하지 못했습니다."
        : result.reason,
    );
  return result.envelope;
}

/** Old tabs must not replace a newer/foreign engine's saved state, even with a current row revision. */
export function mayWriteLegacyPrecisionEnvelope(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    (record(value) &&
      value.format === undefined &&
      value.engine === undefined &&
      (value.schemaVersion === undefined || value.schemaVersion === 1))
  );
}
